import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icons';
import ErrorScreen from './ErrorScreen';
import { photoUrl } from './photoUtils';
import { formatStatus as formatPlantStatus, statusStyle, useStatusMeta } from './statuses';
import { SubstratePicker } from './GrowingMethodPicker';
import { useGuidance } from './GuidanceContext';
import GuidanceSheet from './GuidanceSheet';
import { DATA_HEALTH_GUIDANCE_TITLE, DataHealthGuidanceBody } from './guidanceContent';
import { plantDisplayName } from './nameUtils';

// How many cards to show per round before pausing
const BATCH_SIZE = 5;

// Fixed moss pole options — matches PlantForm.js select options
const MOSS_POLE_OPTIONS = ['Sphagnum', 'Coco Coir', 'Hybrid', 'DIY'];

// All fields shown on every card, in display order.
// Fields not in the plant's gaps array are pre-populated and shown as ✓ completed.
const ALL_FIELDS = [
  { key: 'locations',       label: 'Location',         priority: 'must'     },
  { key: 'light_sources',   label: 'Light Source',     priority: 'must'     },
  { key: 'photos',          label: 'Photos',           priority: 'must'     },
  { key: 'growing_method',  label: 'Growing Method',   priority: 'nice'     },
  { key: 'notes',           label: 'Notes',            priority: 'optional' },
  { key: 'acquisition_date',label: 'Acquisition Date', priority: 'optional' },
  { key: 'source',          label: 'Source',           priority: 'optional' },
  { key: 'cost',            label: 'Cost',             priority: 'optional' },
  { key: 'moss_pole_type',  label: 'Moss Pole',        priority: 'optional' },
  { key: 'cabinet_id',      label: 'Cabinet ID',       priority: 'optional' },
  { key: 'tags',            label: 'Tags',             priority: 'optional' },
];

// ─── Gap field editors ──────────────────────────────────────────────────────
// All editors are fully controlled — no local state. Value lives in HealthCard.
// onChange(value, isDirty) is called on every change.

function LocationEditor({ value, options, onChange }) {
  const toggle = (id) => {
    const next = new Set(value);
    next.has(id) ? next.delete(id) : next.add(id);
    const names = options.filter(o => next.has(o.id)).map(o => o.name);
    onChange({ selectedIds: next, names }, next.size > 0);
  };

  return (
    <div>
      <p className="health-field-label">Location</p>
      <div className="health-chip-row">
        {options.map(loc => (
          <button
            key={loc.id}
            className={`health-chip ${value.has(loc.id) ? 'health-chip--active' : ''}`}
            onClick={() => toggle(loc.id)}
          >
            {loc.name}
          </button>
        ))}
        {options.length === 0 && (
          <span className="health-field-empty">No locations set up yet — add them in Settings</span>
        )}
      </div>
    </div>
  );
}

function LightSourceEditor({ value, options, onChange }) {
  const toggle = (id) => {
    const next = new Set(value);
    next.has(id) ? next.delete(id) : next.add(id);
    const names = options.filter(o => next.has(o.id)).map(o => o.name);
    onChange({ selectedIds: next, names }, next.size > 0);
  };

  return (
    <div>
      <p className="health-field-label">Light Source</p>
      <div className="health-chip-row">
        {options.map(ls => (
          <button
            key={ls.id}
            className={`health-chip ${value.has(ls.id) ? 'health-chip--active' : ''}`}
            onClick={() => toggle(ls.id)}
          >
            {ls.name}
          </button>
        ))}
        {options.length === 0 && (
          <span className="health-field-empty">No light sources set up yet — add them in Settings</span>
        )}
      </div>
    </div>
  );
}

// Growing method editor — substrate chip selector slides in once a method is selected.
// Substrate options are per-method, already nested in the growingMethods lookup.
function GrowingMethodEditor({ methodValue, substrateValue, options, onMethodChange, onSubstrateChange, onSubstrateIncomplete }) {
  const hasMethod = Boolean(methodValue);
  const substrates = options.find(m => m.name === methodValue)?.substrates || [];

  return (
    <div>
      <p className="health-field-label">Growing Method</p>
      <div className="health-chip-row">
        {options.map(gm => (
          <button
            key={gm.id}
            className={`health-chip ${methodValue === gm.name ? 'health-chip--active' : ''}`}
            onClick={() => onMethodChange(methodValue === gm.name ? '' : gm.name, methodValue !== gm.name)}
          >
            {gm.name}
          </button>
        ))}
        {options.length === 0 && (
          <span className="health-field-empty">No growing methods set up yet — add them in Settings</span>
        )}
      </div>

      {/* Substrate slides in when a method is selected */}
      <div className={`health-substrate-reveal ${hasMethod ? 'health-substrate-reveal--visible' : ''}`}>
        {substrates.length > 0 ? (
          <SubstratePicker
            key={methodValue}
            substrates={substrates}
            value={substrateValue}
            onChange={(v) => onSubstrateChange(v, true)}
            onValidityChange={(valid) => onSubstrateIncomplete?.(!valid)}
            label="Substrate"
            methodName={methodValue}
          />
        ) : hasMethod ? (
          <p className="health-field-empty" style={{ marginTop: '0.85rem' }}>
            No substrates for this method — add them in Settings
          </p>
        ) : null}
      </div>
    </div>
  );
}

function NotesEditor({ value, onChange }) {
  return (
    <div>
      <p className="health-field-label">Notes</p>
      <textarea
        className="health-textarea"
        value={value}
        onChange={e => {
          const v = e.target.value;
          onChange(v, v.trim().length > 0);
        }}
        placeholder="Any notes about this plant…"
        rows={3}
      />
    </div>
  );
}

function PhotoEditor({ plantId, uploaded, onResolved }) {
  const [preview,   setPreview]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const inputRef = useRef(null);

  const upload = useCallback((f) => {
    if (!f) return;
    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', f);
    fetch(`/plants/${plantId}/photos`, { method: 'POST', body: formData })
      .then(r => { if (!r.ok) throw new Error('Upload failed'); return r.json(); })
      .then(() => { setUploading(false); onResolved(); })
      .catch(err => { setError(err.message); setUploading(false); });
  }, [plantId, onResolved]);

  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    upload(f);
    e.target.value = '';
  };

  const handleClear = () => {
    setPreview(null);
    setError(null);
  };

  if (uploaded) {
    return (
      <div>
        <p className="health-field-label">Photos</p>
        <div className="health-upload-success">
          <Icon name="check" size={16} /> Photo uploaded successfully
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="health-field-label">Photos</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      {preview ? (
        <div className="round-photo-preview">
          <img src={preview} alt="Selected" className="round-photo-thumb" />
          <div className="round-photo-preview-info">
            {uploading && (
              <span className="round-photo-status round-photo-status--uploading">
                <Icon name="loader" size={13} /> Uploading…
              </span>
            )}
            {error && (
              <span className="round-photo-status round-photo-status--error">
                <Icon name="alertCircle" size={13} /> {error}
              </span>
            )}
            {!uploading && (
              <button className="round-photo-clear" onClick={handleClear} aria-label="Remove photo" title="Remove photo">
                <Icon name="x" size={14} />
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          className="round-photo-btn"
          onClick={() => inputRef.current?.click()}
          aria-label="Add photo"
        >
          <Icon name="camera" size={16} />
          <span>Add photo</span>
        </button>
      )}
      {!error && !preview && (
        <p className="health-upload-hint">Photo gaps auto-resolve on upload — tap Save &amp; Next to continue</p>
      )}
    </div>
  );
}

function AcquisitionDateEditor({ value, onChange }) {
  return (
    <div>
      <p className="health-field-label">Acquisition Date</p>
      <input
        type="date"
        className="health-text-input"
        value={value}
        onChange={e => onChange(e.target.value, Boolean(e.target.value))}
      />
    </div>
  );
}

function SourceEditor({ value, onChange }) {
  return (
    <div>
      <p className="health-field-label">Source</p>
      <input
        type="text"
        className="health-text-input"
        value={value}
        onChange={e => onChange(e.target.value, e.target.value.trim().length > 0)}
        placeholder="e.g. Local nursery, eBay, trade…"
      />
    </div>
  );
}

function CostEditor({ value, onChange }) {
  return (
    <div>
      <p className="health-field-label">Cost ($)</p>
      <input
        type="number"
        className="health-text-input"
        value={value}
        onChange={e => {
          const v = e.target.value;
          onChange(v, v.trim().length > 0);
        }}
        placeholder="0.00"
        min="0"
        step="0.01"
      />
    </div>
  );
}

function MossPoleEditor({ value, onChange }) {
  return (
    <div>
      <p className="health-field-label">Moss Pole</p>
      <div className="health-chip-row">
        {MOSS_POLE_OPTIONS.map(opt => (
          <button
            key={opt}
            className={`health-chip ${value === opt ? 'health-chip--active' : ''}`}
            onClick={() => onChange(value === opt ? '' : opt, value !== opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function CabinetIdEditor({ value, onChange }) {
  return (
    <div>
      <p className="health-field-label">Cabinet ID</p>
      <input
        type="text"
        className="health-text-input"
        value={value}
        onChange={e => onChange(e.target.value, e.target.value.trim().length > 0)}
        placeholder="e.g. Cabinet A, Shelf 2…"
      />
    </div>
  );
}

function TagsEditor({ value, options, onChange }) {
  // value is a Set of tag IDs
  const toggle = (id) => {
    const next = new Set(value);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange({ selectedIds: next, ids: [...next] }, next.size > 0);
  };

  return (
    <div>
      <p className="health-field-label">Tags</p>
      {options.length === 0 ? (
        <span className="health-field-empty">No tags set up yet — add them in Settings</span>
      ) : (
        <div className="health-chip-row">
          {options.map(tag => (
            <button
              key={tag.id}
              className={`health-chip ${value.has(tag.id) ? 'health-chip--active' : ''}`}
              onClick={() => toggle(tag.id)}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Gap pill ───────────────────────────────────────────────────────────────

function GapPill({ gap, state, onClick }) {
  const stateLabel = {
    'active':            'current field',
    'active-saved':      'current field, saved',
    'saved':             'saved',
    'skipped':           'skipped',
    'upcoming':          '',
    'priority-upcoming': '',
  }[state] || '';
  const ariaLabel = stateLabel ? `${gap.label}, ${stateLabel}` : gap.label;
  const prefix = (state === 'saved' || state === 'active-saved')
    ? <span aria-hidden="true">✓ </span>
    : state === 'skipped'
    ? <span aria-hidden="true">– </span>
    : null;
  return (
    <button
      className={`health-gap-pill health-gap-pill--${state}`}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-current={state === 'active' || state === 'active-saved' ? 'true' : undefined}
    >
      {prefix}{gap.label}
    </button>
  );
}

// ─── Health card ────────────────────────────────────────────────────────────

function HealthCard({ item, cardIndex, batchSize, onSave, onSkipPlant, onMarkReviewed, lookups, showPillHint, onPillHintDismiss }) {
  const { gaps, ...plant } = item;
  const hasPriorityGaps = gaps.some(g => g.priority === 'must' || g.priority === 'nice');
  const { byKey: statusByKey } = useStatusMeta();

  // Build the set of gap keys for quick lookup
  const gapKeys = new Set(gaps.map(g => g.key));

  // Full ordered field list for this card — cabinet_id only shown if Grow Cabinet is a location
  const allFields = ALL_FIELDS.filter(f =>
    f.key !== 'cabinet_id' || plant.has_grow_cabinet
  );

  const priorityFields = allFields.filter(f => f.priority !== 'optional');
  const optionalFields = allFields.filter(f => f.priority === 'optional');

  const [gapIdx, setGapIdx] = useState(0);
  const [plantLoading, setPlantLoading] = useState(true);
  const [substrateIncomplete, setSubstrateIncomplete] = useState(false);

  // gapData covers ALL fields, not just gaps — so completed fields can be shown and edited
  const [gapData, setGapData] = useState(() => {
    const init = {};
    allFields.forEach(f => {
      if (f.key === 'locations' || f.key === 'light_sources') {
        init[f.key] = { selectedIds: new Set(), names: [], isDirty: false, isPrePopulated: false };
      } else if (f.key === 'tags') {
        init[f.key] = { selectedIds: new Set(), ids: [], isDirty: false, isPrePopulated: false };
      } else if (f.key === 'photos') {
        init[f.key] = { uploaded: false, isDirty: false, isPrePopulated: false };
      } else {
        init[f.key] = { value: '', isDirty: false, isPrePopulated: false };
      }
    });
    init.substrate = { value: '', isDirty: false, isPrePopulated: false };
    return init;
  });

  // Fetch the full plant record to pre-populate editors with existing values.
  // This ensures the user sees what's already set rather than starting blank.
  useEffect(() => {
    setPlantLoading(true);
    fetch(`/plants/${plant.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(full => {
        if (!full) return;
        setGapData(prev => {
          const next = { ...prev };

          // Locations — seed selectedIds from existing plant_locations
          if (next.locations) {
            const ids = new Set((full.locations || []).map(l => l.id));
            const names = (full.locations || []).map(l => l.name);
            const hasData = ids.size > 0;
            next.locations = { selectedIds: ids, names, isDirty: false, isPrePopulated: hasData };
          }

          // Light sources
          if (next.light_sources) {
            const ids = new Set((full.light_sources || []).map(l => l.id));
            const names = (full.light_sources || []).map(l => l.name);
            const hasData = ids.size > 0;
            next.light_sources = { selectedIds: ids, names, isDirty: false, isPrePopulated: hasData };
          }

          // Tags
          if (next.tags) {
            const ids = new Set((full.tags || []).map(t => t.id));
            const hasData = ids.size > 0;
            next.tags = { selectedIds: ids, ids: [...ids], isDirty: false, isPrePopulated: hasData };
          }

          // Scalar fields — pre-fill from existing record
          const scalars = [
            'growing_method', 'notes', 'acquisition_date',
            'source', 'cost', 'moss_pole_type', 'cabinet_id',
          ];
          scalars.forEach(key => {
            if (next[key] !== undefined) {
              const val = full[key] != null ? String(full[key]) : '';
              next[key] = { value: val, isDirty: false, isPrePopulated: val.length > 0 };
            }
          });

          // Photos — pre-populated if the plant already has any photo
          if (next.photos !== undefined) {
            const hasPhotos = full.cover_photo_id != null;
            next.photos = { uploaded: false, isDirty: false, isPrePopulated: hasPhotos };
          }

          // Substrate always initialized (inline on growing_method card)
          const subVal = full.substrate || '';
          next.substrate = { value: subVal, isDirty: false, isPrePopulated: subVal.length > 0 };

          return next;
        });
      })
      .catch(() => {}) // non-critical — editors just start empty on error
      .finally(() => setPlantLoading(false));
  }, [plant.id]);

  // Track which gap indices have been visited (gapIdx has passed through them)
  // Used to determine pill state for gaps the user jumped over via direct click
  const [visited, setVisited] = useState(new Set());

  const currentGap = allFields[gapIdx];
  const isLastGap  = gapIdx === allFields.length - 1;
  const anyDirty   = Object.values(gapData).some(d => d?.isDirty);

  // Pill state: active > saved (pre-populated or dirty) > skipped (visited+empty) > upcoming
  const pillState = (i) => {
    if (i === gapIdx) {
      const d = gapData[allFields[i].key];
      return (d?.isPrePopulated || d?.isDirty) ? 'active-saved' : 'active';
    }
    const d = gapData[allFields[i].key];
    if (d?.isPrePopulated || d?.isDirty) return 'saved';
    if (!visited.has(i)) return allFields[i].priority !== 'optional' ? 'priority-upcoming' : 'upcoming';
    return 'skipped';
  };

  const goToGap = (targetIdx) => {
    onPillHintDismiss?.();
    const newVisited = new Set(visited);
    const lo = Math.min(gapIdx, targetIdx);
    const hi = Math.max(gapIdx, targetIdx);
    for (let i = lo; i < hi; i++) newVisited.add(i);
    newVisited.add(gapIdx);
    setVisited(newVisited);
    setGapIdx(targetIdx);
  };

  const advanceGap = () => {
    onPillHintDismiss?.();
    setVisited(prev => new Set(prev).add(gapIdx));
    if (!isLastGap) setGapIdx(i => i + 1);
  };

  const handleFieldChange = (key, value, isDirty) => {
    setGapData(prev => ({ ...prev, [key]: { ...prev[key], ...value, isDirty } }));
  };

  const handlePhotoResolved = () => {
    setGapData(prev => ({ ...prev, photos: { uploaded: true, isDirty: true } }));
  };

  const handleSave = () => {
    const payload = { skipped_fields: [] };

    allFields.forEach(f => {
      const d = gapData[f.key] || {};
      // Only send fields the user actually changed — pre-populated unchanged fields are skipped
      if (d.isDirty) {
        if (f.key === 'locations')        payload.locations        = d.names;
        if (f.key === 'light_sources')    payload.light_sources    = d.names;
        if (f.key === 'growing_method')   payload.growing_method   = d.value;
        if (f.key === 'notes')            payload.notes            = d.value;
        if (f.key === 'acquisition_date') payload.acquisition_date = d.value;
        if (f.key === 'source')           payload.source           = d.value;
        if (f.key === 'cost')             payload.cost             = parseFloat(d.value);
        if (f.key === 'moss_pole_type')   payload.moss_pole_type   = d.value;
        if (f.key === 'cabinet_id')       payload.cabinet_id       = d.value;
        if (f.key === 'tags')             payload.tag_ids          = d.ids;
      } else if (gapKeys.has(f.key) && f.key !== 'photos') {
        // Only add to skipped_fields if it was actually a gap the user didn't fill
        payload.skipped_fields.push(f.key);
      }
    });

    // Substrate side-channel — include if user changed it on the growing_method card
    const sub = gapData.substrate;
    if (sub?.isDirty && sub.value.trim()) {
      payload.substrate = sub.value.trim();
    }

    onSave(plant.id, payload);
  };

  return (
    <div className="health-card">
      {/* Progress dots */}
      <div className="health-progress-row">
        <span className="health-progress-label">Plant {cardIndex + 1} of {batchSize}</span>
        <div className="health-progress-dots" aria-hidden="true">
          {Array.from({ length: batchSize }).map((_, i) => (
            <div key={i} className={`health-progress-dot ${
              i < cardIndex ? 'health-progress-dot--done' :
              i === cardIndex ? 'health-progress-dot--active' : ''
            }`} />
          ))}
        </div>
      </div>

      {/* Plant hero */}
      <div className="health-hero">
        {plant.cover_photo_id ? (
          <img
            src={photoUrl(plant.cover_photo_id, plant.cover_photo_rotation_version, plant.cover_photo_created_at)}
            alt={plantDisplayName(plant) || plant.specimen_id}
            className="health-hero-img"
          />
        ) : (
          <div className="health-hero-empty">
            <Icon name="sprout" size={36} />
          </div>
        )}
        <div className="health-hero-overlay">
          <p className="health-hero-name">
            {plantDisplayName(plant)}
          </p>
          <div className="health-hero-meta">
            <span className="specimen-id">{plant.specimen_id}</span>
            <span className={`status-badge status-${plant.status}`} style={statusStyle(plant.status, statusByKey)}>{formatPlantStatus(plant.status, statusByKey)}</span>
          </div>
        </div>
      </div>

      {plantLoading ? (
        <p className="health-loading">Loading plant data…</p>
      ) : (
        <>
          {/* Optional-only callout banner */}
          {!hasPriorityGaps && !plantLoading && (
            <div className="health-optional-callout">
              <Icon name="check" size={13} />
              <span>Required fields are all filled in — everything below is optional</span>
            </div>
          )}

          {showPillHint && (
            <p className="health-pill-hint">Tap any field pill to jump directly to it</p>
          )}

          {/* All fields as pills — completed ones show ✓ green, gaps show as upcoming/active */}
          <div className="health-gap-pills">
            {priorityFields.map((f) => {
              const i = allFields.indexOf(f);
              return (
                <GapPill
                  key={f.key}
                  gap={f}
                  state={pillState(i)}
                  onClick={() => goToGap(i)}
                />
              );
            })}
            {optionalFields.length > 0 && (
              <span className="health-pill-divider">Optional</span>
            )}
            {optionalFields.map((f) => {
              const i = allFields.indexOf(f);
              return (
                <GapPill
                  key={f.key}
                  gap={f}
                  state={pillState(i)}
                  onClick={() => goToGap(i)}
                />
              );
            })}
          </div>

      {/* Active gap editor — value always from gapData, never from editor state */}
      <div className="health-editor-panel">
      <div aria-live="polite" aria-atomic="false">
        {currentGap.key === 'locations' && (
          <LocationEditor
            value={gapData.locations?.selectedIds ?? new Set()}
            options={lookups.locations}
            onChange={(v, d) => handleFieldChange('locations', v, d)}
          />
        )}
        {currentGap.key === 'light_sources' && (
          <LightSourceEditor
            value={gapData.light_sources?.selectedIds ?? new Set()}
            options={lookups.lightSources}
            onChange={(v, d) => handleFieldChange('light_sources', v, d)}
          />
        )}
        {currentGap.key === 'growing_method' && (
          <GrowingMethodEditor
            methodValue={gapData.growing_method?.value ?? plant.growing_method ?? ''}
            substrateValue={gapData.substrate?.value ?? ''}
            options={lookups.growingMethods}
            onMethodChange={(v, d) => { setSubstrateIncomplete(false); handleFieldChange('growing_method', { value: v }, d); }}
            onSubstrateChange={(v, d) => handleFieldChange('substrate', { value: v }, d)}
            onSubstrateIncomplete={setSubstrateIncomplete}
          />
        )}
        {currentGap.key === 'notes' && (
          <NotesEditor
            value={gapData.notes?.value ?? ''}
            onChange={(v, d) => handleFieldChange('notes', { value: v }, d)}
          />
        )}
        {currentGap.key === 'photos' && (
          <PhotoEditor
            plantId={plant.id}
            uploaded={gapData.photos?.uploaded ?? false}
            onResolved={handlePhotoResolved}
          />
        )}
        {currentGap.key === 'acquisition_date' && (
          <AcquisitionDateEditor
            value={gapData.acquisition_date?.value ?? ''}
            onChange={(v, d) => handleFieldChange('acquisition_date', { value: v }, d)}
          />
        )}
        {currentGap.key === 'source' && (
          <SourceEditor
            value={gapData.source?.value ?? ''}
            onChange={(v, d) => handleFieldChange('source', { value: v }, d)}
          />
        )}
        {currentGap.key === 'cost' && (
          <CostEditor
            value={gapData.cost?.value ?? ''}
            onChange={(v, d) => handleFieldChange('cost', { value: v }, d)}
          />
        )}
        {currentGap.key === 'moss_pole_type' && (
          <MossPoleEditor
            value={gapData.moss_pole_type?.value ?? ''}
            onChange={(v, d) => handleFieldChange('moss_pole_type', { value: v }, d)}
          />
        )}
        {currentGap.key === 'cabinet_id' && (
          <CabinetIdEditor
            value={gapData.cabinet_id?.value ?? ''}
            onChange={(v, d) => handleFieldChange('cabinet_id', { value: v }, d)}
          />
        )}
        {currentGap.key === 'tags' && (
          <TagsEditor
            value={gapData.tags?.selectedIds ?? new Set()}
            options={lookups.tags}
            onChange={(v, d) => handleFieldChange('tags', v, d)}
          />
        )}
      </div>
      </div>

      {/* Actions */}
      <div className={`health-actions${isLastGap && anyDirty ? ' health-actions--single' : ''}`}>
        {/* Left button — plant-level escape. Hidden on last field when dirty (right covers save). */}
        {!(isLastGap && anyDirty) && (
          anyDirty ? (
            <button
              className="btn btn-secondary health-skip-btn health-skip-btn--dirty"
              onClick={handleSave}
            >
              Save &amp; Skip Remaining →
            </button>
          ) : (
            <button className="btn btn-ghost health-skip-btn" onClick={onSkipPlant}>
              Skip Plant
            </button>
          )
        )}
        {/* Right button — field-level progression or final save */}
        {!isLastGap ? (
          <button
            className="btn btn-secondary health-next-btn"
            onClick={advanceGap}
            disabled={currentGap.key === 'growing_method' && substrateIncomplete}
          >
            Next field →
          </button>
        ) : (
          <button className="btn btn-primary health-save-btn" onClick={handleSave}>
            Save &amp; Next →
          </button>
        )}
      </div>

      {/* Mark as Reviewed — only when all remaining gaps are optional */}
      {gaps.every(g => g.priority === 'optional') && (
        <div className="health-review-row">
          <button
            className="btn btn-ghost health-review-btn"
            onClick={() => onMarkReviewed(plant.id, plant.genus || plant.common_name || 'Plant')}
          >
            Mark as reviewed — skip optional fields
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
}

// ─── Pause screen ───────────────────────────────────────────────────────────

function PauseScreen({ savedCount, remainingCount, onKeepGoing, onDone }) {
  const allDone = remainingCount === 0;

  return (
    <div className="health-pause">
      <div className="health-pause-icon">
        <Icon name="check" size={28} />
      </div>
      <h2 className="health-pause-title">
        {allDone ? 'All caught up!' : 'Nice work!'}
      </h2>
      <p className="health-pause-body">
        {allDone
          ? 'Every plant has its priority fields filled in.'
          : `${savedCount} plant${savedCount !== 1 ? 's' : ''} updated · ${remainingCount} more with gaps remaining`
        }
      </p>
      {!allDone && (
        <button className="btn btn-primary health-pause-btn" onClick={onKeepGoing}>
          Keep going ({remainingCount} remaining)
        </button>
      )}
      <button className="btn btn-ghost health-pause-btn" onClick={onDone}>
        {allDone ? '← Back to Stats' : 'Done for now'}
      </button>
    </div>
  );
}

// ─── DataHealth screen ──────────────────────────────────────────────────────

export default function DataHealth() {
  const navigate = useNavigate();
  const { hasSeen, markSeen } = useGuidance();

  const [gapItems,  setGapItems]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Lookup options for field editors
  const [lookups, setLookups] = useState({
    locations: [], lightSources: [], growingMethods: [], tags: [],
  });

  // Card flow state
  const [batchStart,     setBatchStart]     = useState(0);
  const [cardIdx,        setCardIdx]        = useState(0);
  const [totalSaved,     setTotalSaved]     = useState(0);
  const [showPause,      setShowPause]      = useState(false);
  const [undoToast,      setUndoToast]      = useState(null); // { plantId, plantName, timerId }
  const [introOpen,      setIntroOpen]      = useState(false);
  const [helpOpen,       setHelpOpen]       = useState(false);

  // ── Fetch gap list + lookups on mount ──────────────────────────────────────
  useEffect(() => {
    Promise.all([
      fetch('/plants/gaps').then(r => {
        if (!r.ok) throw new Error('Failed to load gap data');
        return r.json();
      }),
      fetch('/locations').then(r => r.json()),
      fetch('/light-sources').then(r => r.json()),
      fetch('/growing-methods').then(r => r.json()),
      fetch('/tags').then(r => r.json()),
    ])
      .then(([gaps, locs, ls, gm, tags]) => {
        setGapItems(gaps);
        setLookups({ locations: locs, lightSources: ls, growingMethods: gm, tags });
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // ── Derived batch state ────────────────────────────────────────────────────
  const remaining  = gapItems.slice(batchStart);
  const batch      = remaining.slice(0, BATCH_SIZE);
  const afterBatch = remaining.slice(BATCH_SIZE);
  const currentItem = batch[cardIdx];

  // hasSeen is intentional here: after markSeen() updates guidance state,
  // this re-check prevents a dismissed intro from racing itself open again.
  useEffect(() => {
    if (!loading && currentItem && !hasSeen('seen_health_intro')) {
      setIntroOpen(true);
    }
  }, [loading, currentItem, hasSeen]);

  // ── Save handler ───────────────────────────────────────────────────────────
  const handleSave = (plantId, payload) => {
    setSaving(true);
    setSaveError(null);

    fetch(`/plants/${plantId}/gaps`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(r => {
        if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Save failed'); });
        return r.json();
      })
      .then(updatedPlant => {
        setSaving(false);
        // Remove this plant from the gap list if it now has no gaps
        // (backend re-evaluates; we check by re-fetching would be ideal,
        // but for snappiness we optimistically remove it)
        setGapItems(prev => prev.filter(item => item.id !== plantId));
        advance(true);
      })
      .catch(err => {
        setSaveError(err.message);
        setSaving(false);
      });
  };

  // ── Skip plant handler ────────────────────────────────────────────────────
  const handleSkipPlant = () => advance(false);

  // ── Mark as reviewed handler ──────────────────────────────────────────────
  const handleMarkReviewed = (plantId, plantName) => {
    // Fix 3: if a previous review is still pending, commit it immediately
    // before replacing the toast — prevents the first PATCH becoming un-undoable.
    if (undoToast) {
      clearTimeout(undoToast.timerId);
      fetch(`/plants/${undoToast.plantId}/gaps`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mark_reviewed: true }),
      });
    }

    // Optimistically remove the plant from the gap list
    const newGapItems = gapItems.filter(item => item.id !== plantId);
    setGapItems(newGapItems);

    // Fix 1: do NOT call advance() — that would increment cardIdx against the
    // stale pre-removal batch, skipping the item that just shifted into position.
    // Instead compute the new batch synchronously and only pause when cardIdx
    // is now past its end.
    const newBatch = newGapItems.slice(batchStart, batchStart + BATCH_SIZE);
    if (cardIdx >= newBatch.length) {
      setShowPause(true);
    } else {
      setSaveError(null);
    }

    // Start 5-second undo window — PATCH fires only after timeout.
    // Store a rollback snapshot so fetch failure can fully restore local state.
    const timerId = setTimeout(() => {
      setUndoToast(null);
      fetch(`/plants/${plantId}/gaps`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mark_reviewed: true }),
      });
    }, 5000);
    setUndoToast({
      plantId,
      plantName,
      timerId,
      rollback: { gapItems, cardIdx, batchStart },
    });
  };

  const handleUndoReview = () => {
    if (!undoToast) return;
    clearTimeout(undoToast.timerId);
    const { plantId, rollback } = undoToast;
    setUndoToast(null);
    // Re-fetch to restore the plant at its correct sort position
    fetch('/plants/gaps')
      .then(r => {
        if (!r.ok) throw new Error('Failed to restore gaps');
        return r.json();
      })
      .then(items => {
        setGapItems(items);
        setShowPause(false);
        setSaveError(null);
        // Find the restored plant globally — batchStart may have changed if the
        // user tapped Keep Going after marking the last card in a batch.
        const restoredGlobalIdx = items.findIndex(item => item.id === plantId);
        if (restoredGlobalIdx >= 0) {
          const restoredBatchStart =
            Math.floor(restoredGlobalIdx / BATCH_SIZE) * BATCH_SIZE;
          setBatchStart(restoredBatchStart);
          setCardIdx(restoredGlobalIdx - restoredBatchStart);
        } else {
          // Plant is no longer in gaps (e.g. another session filled its fields);
          // stay in the current batch at the last valid card.
          const newBatch = items.slice(batchStart, batchStart + BATCH_SIZE);
          setCardIdx(Math.max(0, newBatch.length - 1));
        }
      })
      .catch(() => {
        // Refetch failed — restore exact pre-removal local state from snapshot
        setGapItems(rollback.gapItems);
        setBatchStart(rollback.batchStart);
        setCardIdx(rollback.cardIdx);
        setShowPause(false);
        setSaveError('Undo restored locally, but the latest gap list could not be refreshed.');
      });
  };

  const advance = (wasSaved) => {
    const nextCard = cardIdx + 1;
    if (wasSaved) setTotalSaved(n => n + 1);

    if (nextCard >= batch.length) {
      // End of batch — show pause screen
      setShowPause(true);
    } else {
      setCardIdx(nextCard);
      setSaveError(null);
    }
  };

  const handleKeepGoing = () => {
    setBatchStart(b => b + BATCH_SIZE);
    setCardIdx(0);
    setShowPause(false);
    setSaveError(null);
    window.scrollTo(0, 0);
  };

  const handleDone = () => {
    sessionStorage.setItem('healthDismissed', '1');
    navigate('/', { replace: true });
  };

  const dismissIntro = () => {
    setIntroOpen(false);
  };

  const dismissPillHint = () => {
    if (!hasSeen('seen_pill_hint')) markSeen('seen_pill_hint');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="app">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => navigate('/', { replace: true })} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Data Health</h1>
          </div>
          <div className="sub-header-actions">
            <button className="sub-header-action" onClick={() => setHelpOpen(true)} aria-label="How Data Health works" title="How Data Health works">
              <Icon name="helpCircle" size={18} />
            </button>
          </div>
        </header>
        <p className="health-loading">Loading…</p>
        <GuidanceSheet
          variant="sheet"
          isOpen={helpOpen}
          onDismiss={() => setHelpOpen(false)}
          title={DATA_HEALTH_GUIDANCE_TITLE}
          primaryAction={{ label: 'Got it', onClick: () => setHelpOpen(false) }}
        >
          <DataHealthGuidanceBody />
        </GuidanceSheet>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => navigate('/', { replace: true })} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Data Health</h1>
          </div>
          <div className="sub-header-actions">
            <button className="sub-header-action" onClick={() => setHelpOpen(true)} aria-label="How Data Health works" title="How Data Health works">
              <Icon name="helpCircle" size={18} />
            </button>
          </div>
        </header>
        <ErrorScreen message={error} />
        <GuidanceSheet
          variant="sheet"
          isOpen={helpOpen}
          onDismiss={() => setHelpOpen(false)}
          title={DATA_HEALTH_GUIDANCE_TITLE}
          primaryAction={{ label: 'Got it', onClick: () => setHelpOpen(false) }}
        >
          <DataHealthGuidanceBody />
        </GuidanceSheet>
      </div>
    );
  }

  const allDone = gapItems.length === 0;

  return (
    <div className="app">
      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate('/', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Data Health</h1>
        </div>
        <div className="sub-header-actions">
          <button className="sub-header-action" onClick={() => setHelpOpen(true)} aria-label="How Data Health works" title="How Data Health works">
            <Icon name="helpCircle" size={18} />
          </button>
        </div>
      </header>

      {/* Summary row — shown while working through cards */}
      {!showPause && currentItem && (
        <div className="health-summary-bar">
          <Icon name="clipboardCheck" size={14} />
          <span>
            <strong>{gapItems.length}</strong> plant{gapItems.length !== 1 ? 's' : ''} to review
            {' · '}showing <strong>{batch.length}</strong> now
          </span>
        </div>
      )}

      {/* All done state */}
      {(allDone || (showPause && afterBatch.length === 0)) && (
        <PauseScreen
          savedCount={totalSaved}
          remainingCount={0}
          onKeepGoing={handleKeepGoing}
          onDone={handleDone}
        />
      )}

      {/* Mid-batch pause */}
      {showPause && afterBatch.length > 0 && (
        <PauseScreen
          savedCount={totalSaved}
          remainingCount={afterBatch.length}
          onKeepGoing={handleKeepGoing}
          onDone={handleDone}
        />
      )}

      {/* Active card */}
      {!showPause && currentItem && (
        <>
          {saveError && (
            <p className="health-save-error">{saveError}</p>
          )}
          <HealthCard
            key={`${currentItem.id}-${batchStart}`}
            item={currentItem}
            cardIndex={cardIdx}
            batchSize={batch.length}
            onSave={handleSave}
            onSkipPlant={handleSkipPlant}
            onMarkReviewed={handleMarkReviewed}
            lookups={lookups}
            showPillHint={cardIdx === 0 && !introOpen && !hasSeen('seen_pill_hint')}
            onPillHintDismiss={dismissPillHint}
          />
          {saving && <p className="health-saving">Saving…</p>}
        </>
      )}

      {/* Undo toast — 5-second window after "Mark as reviewed" */}
      {undoToast && (
        <div className="toast toast--undo" role="status" aria-live="polite">
          <span>"{undoToast.plantName}" marked as reviewed</span>
          <button className="toast-undo-btn" onClick={handleUndoReview}>Undo</button>
        </div>
      )}

      <GuidanceSheet
        variant="sheet"
        isOpen={introOpen}
        onDismiss={() => setIntroOpen(false)}
        title={DATA_HEALTH_GUIDANCE_TITLE}
        dismissKey="seen_health_intro"
        primaryAction={{ label: 'Start reviewing →', onClick: dismissIntro }}
      >
        <DataHealthGuidanceBody />
      </GuidanceSheet>

      <GuidanceSheet
        variant="sheet"
        isOpen={helpOpen}
        onDismiss={() => setHelpOpen(false)}
        title={DATA_HEALTH_GUIDANCE_TITLE}
        primaryAction={{ label: 'Got it', onClick: () => setHelpOpen(false) }}
      >
        <DataHealthGuidanceBody />
      </GuidanceSheet>
    </div>
  );
}
